import React from 'react';
import { Link, Text, View } from '@react-pdf/renderer';
import { SQUAD_PDF_FONT_FAMILY } from './templateConstants';

/** Tags/attrs we allow from the Quill editor into preview + PDF. */
const ALLOWED_TAGS = new Set([
  'P',
  'BR',
  'DIV',
  'SPAN',
  'A',
  'STRONG',
  'B',
  'EM',
  'I',
  'U',
  'S',
  'STRIKE',
  'DEL',
]);

/** Default link color when Quill did not set an explicit text color. */
const DEFAULT_LINK_COLOR = '#0645AD';

type RichMark = {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  color?: string;
  backgroundColor?: string;
  href?: string;
};

type RichSpan = RichMark & { text: string };

type RichParagraph = { spans: RichSpan[] };

const isLikelyHtml = (value: string): boolean => /<\/?[a-z][\s\S]*>/i.test(value);

/**
 * Quill reports every content change with its source. Only 'user' is the author;
 * 'api' is Quill itself — the empty `<p><br></p>` it writes when it mounts, and the
 * echo of the document text we load into it. Passing an 'api' change on to the form
 * let that empty mount value arrive after the document had loaded and blank the
 * field (Wichtige Hinweise, Erläuterungen, Fondsberater): the editor still showed the
 * text, the PDF was empty, and the next save wrote the empty value.
 */
export const isAuthorEdit = (source?: string): boolean => source === 'user';

/** Convert legacy plain text / `**bold**` into simple HTML paragraphs. */
export const plainTextToRichHtml = (value: string): string => {
  const raw = String(value || '');
  if (!raw.trim()) return '';
  if (isLikelyHtml(raw)) return raw;

  return raw
    .split(/\n/)
    .map((line) => {
      const escaped = line
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      const withBold = escaped.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      return `<p>${withBold || '<br/>'}</p>`;
    })
    .join('');
};

const normalizeColor = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  const v = value.trim().toLowerCase();
  if (!v || v === 'transparent' || v === 'inherit' || v === 'initial') return undefined;
  return value.trim();
};

/** Allow only safe absolute / mailto URLs for preview + PDF links. */
export const sanitizeHref = (raw?: string | null): string | undefined => {
  if (!raw) return undefined;
  let href = String(raw).trim();
  if (!href) return undefined;

  const lower = href.toLowerCase();
  if (
    lower.startsWith('javascript:') ||
    lower.startsWith('data:') ||
    lower.startsWith('vbscript:') ||
    lower.startsWith('file:')
  ) {
    return undefined;
  }

  if (href.startsWith('//')) href = `https:${href}`;
  else if (/^www\./i.test(href)) href = `https://${href}`;

  if (/^https?:\/\//i.test(href) || /^mailto:/i.test(href)) return href;
  return undefined;
};

const parseStyleAttr = (styleAttr: string | null): Pick<RichMark, 'color' | 'backgroundColor'> => {
  if (!styleAttr) return {};
  const out: Pick<RichMark, 'color' | 'backgroundColor'> = {};
  styleAttr.split(';').forEach((part) => {
    const [rawKey, ...rest] = part.split(':');
    if (!rawKey || !rest.length) return;
    const key = rawKey.trim().toLowerCase();
    const val = rest.join(':').trim();
    if (key === 'color') out.color = normalizeColor(val);
    if (key === 'background' || key === 'background-color') out.backgroundColor = normalizeColor(val);
  });
  return out;
};

const mergeMarks = (base: RichMark, next: RichMark): RichMark => ({
  bold: base.bold || next.bold,
  italic: base.italic || next.italic,
  underline: base.underline || next.underline,
  strike: base.strike || next.strike,
  color: next.color || base.color,
  backgroundColor: next.backgroundColor || base.backgroundColor,
  href: next.href || base.href,
});

const sameMarks = (a: RichMark, b: RichMark): boolean =>
  a.bold === b.bold &&
  a.italic === b.italic &&
  a.underline === b.underline &&
  a.strike === b.strike &&
  a.color === b.color &&
  a.backgroundColor === b.backgroundColor &&
  a.href === b.href;

const pushText = (spans: RichSpan[], text: string, marks: RichMark) => {
  if (!text) return;
  const last = spans[spans.length - 1];
  if (last && sameMarks(last, marks)) {
    last.text += text;
    return;
  }
  spans.push({ text, ...marks });
};

const walkNode = (node: Node, marks: RichMark, spans: RichSpan[]) => {
  if (node.nodeType === Node.TEXT_NODE) {
    pushText(spans, node.textContent || '', marks);
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;

  const el = node as HTMLElement;
  const tag = el.tagName.toUpperCase();

  if (tag === 'A') {
    const href = sanitizeHref(el.getAttribute('href'));
    const styleMarks = parseStyleAttr(el.getAttribute('style'));
    const next = mergeMarks(marks, {
      href,
      underline: Boolean(href) || marks.underline,
      color: styleMarks.color,
      backgroundColor: styleMarks.backgroundColor,
    });
    Array.from(el.childNodes).forEach((child) => walkNode(child, next, spans));
    return;
  }

  if (!ALLOWED_TAGS.has(tag)) {
    Array.from(el.childNodes).forEach((child) => walkNode(child, marks, spans));
    return;
  }

  if (tag === 'BR') {
    pushText(spans, '\n', marks);
    return;
  }

  const styleMarks = parseStyleAttr(el.getAttribute('style'));
  const next: RichMark = mergeMarks(marks, {
    bold: tag === 'STRONG' || tag === 'B' || marks.bold,
    italic: tag === 'EM' || tag === 'I' || marks.italic,
    underline: tag === 'U' || marks.underline,
    strike: tag === 'S' || tag === 'STRIKE' || tag === 'DEL' || marks.strike,
    color: styleMarks.color,
    backgroundColor: styleMarks.backgroundColor,
  });

  Array.from(el.childNodes).forEach((child) => walkNode(child, next, spans));
};

const NBSP = '\u00a0';

/**
 * A protected space the author typed (Option+Space, `&nbsp;`) must survive into the
 * PDF — that is the whole point of the character, and react-pdf honours it: its line
 * breaker splits on U+0020 only, so "31. Dezember" stays on one line.
 *
 * Quill also writes `&nbsp;` where a space would collapse in HTML — leading, trailing
 * and doubled spaces. Those are markup artefacts, not an instruction, and turning them
 * into protected spaces would glue words together that the author never tied. The rule
 * below keeps the character exactly where it stands between two non-space characters
 * and relaxes it everywhere else.
 */
const keepAuthoredNbsp = (spans: RichSpan[]): RichSpan[] => {
  const full = spans.map((s) => s.text).join('');
  if (!full.includes(NBSP)) return spans.map((s) => ({ ...s }));

  const isSpace = (ch: string | undefined) => ch === undefined || ch === ' ' || ch === NBSP;
  const chars = Array.from(full);
  const relaxed = chars
    .map((ch, i) =>
      ch === NBSP && (isSpace(chars[i - 1]) || isSpace(chars[i + 1])) ? ' ' : ch,
    )
    .join('');

  let at = 0;
  return spans.map((s) => {
    const next = { ...s, text: relaxed.slice(at, at + s.text.length) };
    at += s.text.length;
    return next;
  });
};

/** Parse Quill/legacy HTML into paragraph + styled span model. */
export const parseRichHtml = (htmlOrPlain: string): RichParagraph[] => {
  const html = plainTextToRichHtml(htmlOrPlain);
  if (!html.trim() || typeof DOMParser === 'undefined') {
    if (!String(htmlOrPlain || '').trim()) return [];
    return [{ spans: [{ text: String(htmlOrPlain) }] }];
  }

  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild || doc.body;
  const paragraphs: RichParagraph[] = [];

  const flushParagraph = (spans: RichSpan[]) => {
    const cleaned = keepAuthoredNbsp(spans).filter((s) => s.text.length > 0);
    if (!cleaned.length) {
      paragraphs.push({ spans: [{ text: ' ' }] });
      return;
    }
    // A break that ends the paragraph has no effect in HTML either. Quill writes one
    // at the end of a block when the author pressed Shift+Enter there; without this
    // the PDF would show a blank line the editor never displayed.
    const last = cleaned[cleaned.length - 1];
    last.text = last.text.replace(/\n+$/, '');
    const trimmed = cleaned.filter((s) => s.text.length > 0);
    paragraphs.push({ spans: trimmed.length ? trimmed : [{ text: ' ' }] });
  };

  const blockChildren = Array.from(root.childNodes);
  const hasBlock = blockChildren.some(
    (n) => n.nodeType === Node.ELEMENT_NODE && ['P', 'DIV'].includes((n as HTMLElement).tagName),
  );

  if (!hasBlock) {
    const spans: RichSpan[] = [];
    walkNode(root, {}, spans);
    flushParagraph(spans);
    return paragraphs;
  }

  blockChildren.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const t = (node.textContent || '').trim();
      if (t) flushParagraph([{ text: t }]);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    const tag = el.tagName.toUpperCase();
    if (tag === 'BR') {
      flushParagraph([{ text: ' ' }]);
      return;
    }
    if (tag === 'P' || tag === 'DIV') {
      const spans: RichSpan[] = [];
      Array.from(el.childNodes).forEach((child) => walkNode(child, {}, spans));
      flushParagraph(spans);
      return;
    }
    const spans: RichSpan[] = [];
    walkNode(el, {}, spans);
    flushParagraph(spans);
  });

  return paragraphs.length ? paragraphs : [];
};

/** Strip disallowed tags/attrs for safe web preview. */
export const sanitizeRichHtml = (htmlOrPlain: string): string => {
  const html = plainTextToRichHtml(htmlOrPlain);
  if (!html.trim() || typeof DOMParser === 'undefined') return html;

  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild || doc.body;

  const clean = (node: Node): Node | null => {
    if (node.nodeType === Node.TEXT_NODE) return doc.createTextNode(node.textContent || '');
    if (node.nodeType !== Node.ELEMENT_NODE) return null;

    const el = node as HTMLElement;
    const tag = el.tagName.toUpperCase();

    if (tag === 'A') {
      const href = sanitizeHref(el.getAttribute('href'));
      if (!href) {
        const frag = doc.createDocumentFragment();
        Array.from(el.childNodes).forEach((child) => {
          const c = clean(child);
          if (c) frag.appendChild(c);
        });
        return frag;
      }
      const next = doc.createElement('a');
      next.setAttribute('href', href);
      next.setAttribute('target', '_blank');
      next.setAttribute('rel', 'noopener noreferrer');
      const style = parseStyleAttr(el.getAttribute('style'));
      const parts: string[] = [];
      if (style.color) parts.push(`color: ${style.color}`);
      if (style.backgroundColor) parts.push(`background-color: ${style.backgroundColor}`);
      if (parts.length) next.setAttribute('style', parts.join('; '));
      Array.from(el.childNodes).forEach((child) => {
        const c = clean(child);
        if (c) next.appendChild(c);
      });
      return next;
    }

    if (!ALLOWED_TAGS.has(tag)) {
      const frag = doc.createDocumentFragment();
      Array.from(el.childNodes).forEach((child) => {
        const c = clean(child);
        if (c) frag.appendChild(c);
      });
      return frag;
    }

    const next = doc.createElement(tag === 'DIV' ? 'p' : tag.toLowerCase());
    if (tag === 'SPAN' || tag === 'P' || tag === 'DIV') {
      const style = parseStyleAttr(el.getAttribute('style'));
      const parts: string[] = [];
      if (style.color) parts.push(`color: ${style.color}`);
      if (style.backgroundColor) parts.push(`background-color: ${style.backgroundColor}`);
      if (parts.length) next.setAttribute('style', parts.join('; '));
    }
    Array.from(el.childNodes).forEach((child) => {
      const c = clean(child);
      if (c) next.appendChild(c);
    });
    return next;
  };

  const out = doc.createElement('div');
  Array.from(root.childNodes).forEach((child) => {
    const c = clean(child);
    if (c) out.appendChild(c);
  });
  return out.innerHTML;
};

const pdfFontFamily = (_marks: RichMark): string => SQUAD_PDF_FONT_FAMILY;

const pdfFontWeight = (marks: RichMark): number => (marks.bold ? 700 : 400);

const pdfFontStyle = (marks: RichMark): 'normal' | 'italic' => (marks.italic ? 'italic' : 'normal');

const pdfTextDecoration = (
  marks: RichMark,
): 'underline' | 'line-through' | 'underline line-through' | undefined => {
  if (marks.underline && marks.strike) return 'underline line-through';
  if (marks.underline || marks.href) return 'underline';
  if (marks.strike) return 'line-through';
  return undefined;
};

const pdfSpanStyle = (span: RichSpan) => ({
  fontFamily: pdfFontFamily(span),
  fontWeight: pdfFontWeight(span),
  fontStyle: pdfFontStyle(span),
  color: span.color || (span.href ? DEFAULT_LINK_COLOR : undefined),
  backgroundColor: span.backgroundColor,
  textDecoration: pdfTextDecoration(span),
});

/** react-pdf renderer for restricted rich HTML (links use Link → clickable in PDF). */
export const RichHtmlPdf: React.FC<{
  html: string;
  style?: Record<string, any>;
}> = ({ html, style }) => {
  const paragraphs = parseRichHtml(html);
  if (!paragraphs.length) return null;

  return (
    <View>
      {paragraphs.map((p, pi) => (
        <Text key={pi} style={{ ...style, marginBottom: pi === paragraphs.length - 1 ? 0 : 6 }}>
          {p.spans.map((span, si) =>
            span.href ? (
              <Link key={si} src={span.href} style={pdfSpanStyle(span)}>
                {span.text}
              </Link>
            ) : (
              <Text key={si} style={pdfSpanStyle(span)}>
                {span.text}
              </Text>
            ),
          )}
        </Text>
      ))}
    </View>
  );
};

/** Web preview box content (sanitized HTML). */
export const richHtmlForPreview = (htmlOrPlain: string): string => sanitizeRichHtml(htmlOrPlain);

export type { RichParagraph, RichSpan };
